import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  FolderTree,
  ImageIcon,
  Layers3,
  Pencil,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  categoriesApi,
  getApiValidationErrors,
  menusApi,
} from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Categories/categories.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const CATEGORIES_PER_PAGE = 10;

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};


function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = '—',
  ariaLabel,
  className = '',
  portal = false,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  // Position table dropdowns outside the table while preserving
  // the exact same visual structure as the Menus dropdown.
  const updatePortalPosition = () => {
    if (!portal || !rootRef.current) {
      return;
    }

    const rect =
      rootRef.current.getBoundingClientRect();

    const direction =
      window.getComputedStyle(rootRef.current)
        .direction ||
      document.documentElement.dir ||
      'ltr';

    const viewportPadding = 8;
    const gap = 8;
    const estimatedMenuHeight = Math.min(
      options.length * 38 +
        Math.max(options.length - 1, 0) * 4 +
        12,
      240,
    );

    const spaceBelow =
      window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openAbove =
      spaceBelow < estimatedMenuHeight + gap &&
      spaceAbove > spaceBelow;

    let top = openAbove
      ? rect.top - estimatedMenuHeight - gap
      : rect.bottom + gap;

    top = Math.max(
      viewportPadding,
      Math.min(
        top,
        window.innerHeight -
          estimatedMenuHeight -
          viewportPadding,
      ),
    );

    const width = rect.width;

    let left =
      direction === 'rtl'
        ? rect.right - width
        : rect.left;

    left = Math.max(
      viewportPadding,
      Math.min(
        left,
        window.innerWidth -
          width -
          viewportPadding,
      ),
    );

    setPortalStyle({
      top,
      left,
      width,
      direction,
      placement: openAbove
        ? 'above'
        : 'below',
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger =
        rootRef.current?.contains(event.target);

      const clickedPortal =
        menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPortal) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return undefined;
    }

    updatePortalPosition();

    const frame = window.requestAnimationFrame(
      updatePortalPosition,
    );

    window.addEventListener(
      'resize',
      updatePortalPosition,
    );
    window.addEventListener(
      'scroll',
      updatePortalPosition,
      true,
    );

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener(
        'resize',
        updatePortalPosition,
      );
      window.removeEventListener(
        'scroll',
        updatePortalPosition,
        true,
      );
    };
    // Reposition when the menu opens or its size can change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal, options.length]);

  const renderOptions = () =>
    options.map((option) => {
      const active =
        String(option.value) ===
        String(value);

      return (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={active}
          className={
            active
              ? 'is-active'
              : ''
          }
          onClick={() => {
            onChange?.(
              option.value,
            );

            setOpen(false);
          }}
        >
          <span>
            {option.label}
          </span>

          {active ? (
            <Check size={15} />
          ) : null}
        </button>
      );
    });

  const portalMenu =
    open &&
    portal &&
    portalStyle
      ? createPortal(
          <div
            ref={menuRef}
            className={`categories-custom-select__menu categories-custom-select__menu--portal ${
              portalStyle.placement === 'above'
                ? 'is-above'
                : 'is-below'
            }`}
            role="listbox"
            dir={portalStyle.direction}
            style={{
              top: `${portalStyle.top}px`,
              left: `${portalStyle.left}px`,
              width: `${portalStyle.width}px`,
            }}
          >
            {renderOptions()}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`categories-custom-select ${className}`}
      >
        <button
          type="button"
          className={`categories-custom-select__trigger ${
            open ? 'is-open' : ''
          }`}
          onClick={() => {
            if (!disabled) {
              if (!open && portal) {
                updatePortalPosition();
              }

              setOpen(
                (current) =>
                  !current,
              );
            }
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>
            {selectedOption?.label ||
              placeholder}
          </span>

          <ChevronDown
            size={16}
            className={
              open
                ? 'is-rotated'
                : ''
            }
          />
        </button>

        {open && !portal ? (
          <div
            className="categories-custom-select__menu"
            role="listbox"
          >
            {renderOptions()}
          </div>
        ) : null}
      </div>

      {portalMenu}
    </>
  );
}

function Categories() {
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();

  const direction = String(language || '').toLowerCase().startsWith('ar')
    ? 'rtl'
    : 'ltr';
  const isRtl = direction === 'rtl';

  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [meta, setMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedMenuId, setSelectedMenuId] = useState(
    localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '',
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');

  const [loading, setLoading] = useState(true);
  const [menusLoading, setMenusLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) => String(menu.id) === String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const selectedMenuName =
    getTranslatedField(
      selectedMenu,
      'name',
      language,
      'fr',
    ) ||
    selectedMenu?.name ||
    '—';

  const selectedMenuDescription =
    getTranslatedField(
      selectedMenu,
      'description',
      language,
      'fr',
    ) ||
    selectedMenu?.description ||
    '';

  const filters = useMemo(
    () => ({
      menu_id: selectedMenuId || undefined,

      locale: language,

      search: searchTerm || undefined,

      is_active:
        selectedStatus === ''
          ? undefined
          : selectedStatus === 'active'
            ? 1
            : 0,

      parent_only: selectedType === 'main' ? 1 : undefined,

      children_only: selectedType === 'sub' ? 1 : undefined,

      page: currentPage,
      per_page: CATEGORIES_PER_PAGE,
    }),
    [
      selectedMenuId,
      language,
      searchTerm,
      selectedStatus,
      selectedType,
      currentPage,
    ],
  );

  // Load menus for the current establishment.
  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response = await menusApi.getMenus(
        establishmentId,
        {
          per_page: 100,
          locale: language,
        },
      );

      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus = Array.isArray(responseMenus)
        ? responseMenus
        : [];

      setMenus(normalizedMenus);

      const storedMenuId = localStorage.getItem(
        SELECTED_MENU_STORAGE_KEY,
      );

      const storedMenuExists = normalizedMenus.some(
        (menu) => String(menu.id) === String(storedMenuId),
      );

      if (storedMenuExists) {
        setSelectedMenuId(String(storedMenuId));
        return;
      }

      const defaultMenu =
        normalizedMenus.find((menu) => Boolean(menu.is_default)) ||
        normalizedMenus.find((menu) => Boolean(menu.is_active)) ||
        normalizedMenus[0];

      if (defaultMenu?.id) {
        const menuId = String(defaultMenu.id);

        localStorage.setItem(
          SELECTED_MENU_STORAGE_KEY,
          menuId,
        );

        setSelectedMenuId(menuId);
      } else {
        localStorage.removeItem(
          SELECTED_MENU_STORAGE_KEY,
        );

        setSelectedMenuId('');
      }
    } catch (apiError) {
      const translatedError = translateApiError(
        apiError,
        t,
      );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setMenusLoading(false);
    }
  };

  // Load categories with the selected menu and filters.
  const fetchCategories = async () => {
    if (!selectedMenuId) {
      setCategories([]);
      setMeta(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setValidationErrors({});

      const response = await categoriesApi.getCategories(
        establishmentId,
        filters,
      );

      setCategories(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (apiError) {
      const translatedError = translateApiError(
        apiError,
        t,
      );

      setError(translatedError);

      setValidationErrors(
        getApiValidationErrors(apiError),
      );

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId, language]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCategories();
    }, 350);

    return () => clearTimeout(timeout);
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, selectedType]);

  useEffect(() => {
    const lastPage = Number(meta?.last_page || 1);

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [meta?.last_page, currentPage]);

  // Change the active menu for category management.
  const handleMenuChange = (menuId) => {

    setSelectedMenuId(menuId);

    setSearchTerm('');
    setSelectedStatus('');
    setSelectedType('');
    setCurrentPage(1);

    if (menuId) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  // Delete category using the global confirmation dialog.
  const handleDelete = async (category) => {
    const categoryName =
      getTranslatedField(
        category,
        'name',
        language,
        'fr',
      ) ||
      t('categories.noName');

    const confirmed = await confirm({
      title: t(
        'categories.actions.delete',
      ),
      message: t(
        'categories.confirmDelete',
      ),
      itemName: categoryName,
      confirmText: t(
        'categories.actions.delete',
      ),
      cancelText: t(
        'common.cancel',
      ),
      closeText: t(
        'common.close',
      ),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await categoriesApi.deleteCategory(
        category.id,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t(
          'categories.alerts.deleted',
        ),
      });

      await fetchCategories();
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(
        translatedError,
      );

      setValidationErrors(
        getApiValidationErrors(
          apiError,
        ),
      );

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message:
          translatedError,
      });
    }
  };

  // Toggle active/inactive category status.
  const handleStatusChange = async (
    categoryId,
    value,
  ) => {
    try {
      const isActive = value === 'active';

      await categoriesApi.updateStatus(
        categoryId,
        isActive,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t(
          'categories.alerts.statusUpdated',
        ),
      });

      await fetchCategories();
    } catch (apiError) {
      const translatedError = translateApiError(
        apiError,
        t,
      );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    }
  };

  const mainCategoriesCount = categories.filter(
    (category) => !category.parent_id,
  ).length;

  const subcategoriesCount = categories.filter(
    (category) => category.parent_id,
  ).length;

  const categoriesWithImagesCount = categories.filter(
    (category) =>
      category.image_url ||
      category.image ||
      category.image_path,
  ).length;

  const totalPages = Math.max(
    1,
    Number(meta?.last_page || 1),
  );

  const activePage = Math.min(
    Math.max(1, Number(meta?.current_page || currentPage)),
    totalPages,
  );

  const paginationStart =
    Number(meta?.from || 0);

  const paginationEnd =
    Number(meta?.to || 0);

  const paginationTotal =
    Number(meta?.total || categories.length);

  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;

    const firstPage = Math.max(
      1,
      Math.min(
        activePage - 2,
        totalPages - maxVisiblePages + 1,
      ),
    );

    const lastPage = Math.min(
      totalPages,
      firstPage + maxVisiblePages - 1,
    );

    return Array.from(
      {
        length: Math.max(
          0,
          lastPage - firstPage + 1,
        ),
      },
      (_, index) => firstPage + index,
    );
  }, [activePage, totalPages]);

  return (
    <div className="categories-page" dir={direction}>
      <section className="categories-heading">
        <div>
          <p className="categories-heading__eyebrow">
            {t('categories.eyebrow')}
          </p>

          <h1>{t('categories.title')}</h1>

          <p>{t('categories.description')}</p>
        </div>

        <Link
          to="/categories/new"
          className="dashboard-primary-button"
        >
          <Plus size={18} />
          {t('categories.addCategory')}
        </Link>
      </section>

      <section className="categories-menu-context">
        <div className="categories-menu-context__icon">
          <BookOpen size={22} />
        </div>

        <div className="categories-menu-context__content">
          <span>{t('menus.title')}</span>

          <strong>
            {selectedMenuName}
          </strong>

          {selectedMenuDescription ? (
            <small>{selectedMenuDescription}</small>
          ) : null}
        </div>

        <CustomSelect
          value={selectedMenuId}
          onChange={handleMenuChange}
          disabled={menusLoading}
          ariaLabel={t('menus.title')}
          className="categories-menu-context__select"
          placeholder={
            menusLoading
              ? t('menus.loading')
              : t('menus.empty.title')
          }
          options={menus.map(
            (menu) => ({
              value: String(menu.id),
              label:
                `${
                  getTranslatedField(
                    menu,
                    'name',
                    language,
                    'fr',
                  ) ||
                  menu.name ||
                  '—'
                }${
                  menu.is_default
                    ? ` • ${t(
                        'menus.status.default',
                      )}`
                    : ''
                }`,
            }),
          )}
        />
      </section>

      <section className="categories-summary-grid">
        <article className="categories-summary-card">
          <span>
            <FolderTree size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(
                meta?.total || categories.length,
                language,
              )}
            </strong>

            <p>{t('categories.summary.total')}</p>
          </div>
        </article>

        <article className="categories-summary-card">
          <span>
            <Layers3 size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(mainCategoriesCount, language)}
            </strong>

            <p>
              {t(
                'categories.summary.mainCategories',
              )}
            </p>
          </div>
        </article>

        <article className="categories-summary-card">
          <span>
            <FolderTree size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(subcategoriesCount, language)}
            </strong>

            <p>
              {t(
                'categories.summary.subcategories',
              )}
            </p>
          </div>
        </article>

        <article className="categories-summary-card">
          <span>
            <ImageIcon size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(categoriesWithImagesCount, language)}
            </strong>

            <p>
              {t(
                'categories.summary.withImages',
              )}
            </p>
          </div>
        </article>
      </section>

      <section className="categories-panel">
        <div className="categories-toolbar">
          <div className="categories-search">
            <Search size={18} />

            <input
              type="search"
              dir={direction}
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
              placeholder={t(
                'categories.searchPlaceholder',
              )}
              disabled={!selectedMenuId}
            />
          </div>

          <CustomSelect
            value={selectedType}
            onChange={setSelectedType}
            className="categories-filter"
            disabled={!selectedMenuId}
            ariaLabel={t(
              'categories.filters.allTypes',
            )}
            options={[
              {
                value: '',
                label: t(
                  'categories.filters.allTypes',
                ),
              },
              {
                value: 'main',
                label: t(
                  'categories.details.mainCategory',
                ),
              },
              {
                value: 'sub',
                label: t(
                  'categories.details.subcategory',
                ),
              },
            ]}
          />

          <CustomSelect
            value={selectedStatus}
            onChange={setSelectedStatus}
            className="categories-filter"
            disabled={!selectedMenuId}
            ariaLabel={t(
              'categories.filters.allStatuses',
            )}
            options={[
              {
                value: '',
                label: t(
                  'categories.filters.allStatuses',
                ),
              },
              {
                value: 'active',
                label: t(
                  'categories.status.active',
                ),
              },
              {
                value: 'inactive',
                label: t(
                  'categories.status.inactive',
                ),
              },
            ]}
          />
        </div>

        {error ? (
          <div className="products-alert products-alert--error">
            {error}
          </div>
        ) : null}

        {validationErrors.category ? (
          <div className="products-alert products-alert--error">
            {validationErrors.category[0]}
          </div>
        ) : null}

        {!selectedMenuId && !menusLoading ? (
          <div className="products-empty-state">
            <BookOpen size={34} />

            <h2>{t('menus.empty.title')}</h2>

            <p>{t('menus.empty.description')}</p>

            <Link
              to="/menus/new"
              className="dashboard-primary-button"
            >
              <Plus size={18} />
              {t('menus.addMenu')}
            </Link>
          </div>
        ) : loading ? (
          <div className="products-empty-state">
            <RefreshCcw
              size={34}
              className="menus-loading-icon"
            />

            <h2>{t('categories.loading')}</h2>
          </div>
        ) : categories.length > 0 ? (
          <div className="categories-table-wrapper">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>
                    {t('categories.table.category')}
                  </th>

                  <th>
                    {t('categories.details.type')}
                  </th>

                  <th>
                    {t(
                      'categories.details.parentCategory',
                    )}
                  </th>

                  <th>
                    {t('categories.table.products')}
                  </th>

                  <th>
                    {t('categories.table.sortOrder')}
                  </th>

                  <th>
                    {t('categories.table.status')}
                  </th>

                  <th>
                    {t('categories.table.actions')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {categories.map((category) => {
                  const categoryName =
                    getTranslatedField(
                      category,
                      'name',
                      language,
                      'fr',
                    );

                  const categoryDescription =
                    getTranslatedField(
                      category,
                      'description',
                      language,
                      'fr',
                    );

                  const parentName =
                    getTranslatedField(
                      category.parent,
                      'name',
                      language,
                      'fr',
                    );

                  const imageUrl = resolveMediaUrl(
                    category.image_url ||
                      category.image ||
                      category.image_path,
                  );

                  return (
                    <tr key={category.id}>
                      <td>
                        <div className="categories-table__category">
                          <span className="categories-table__image">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={categoryName}
                              />
                            ) : (
                              <FolderTree size={18} />
                            )}
                          </span>

                          <div>
                            <strong>
                              {categoryName ||
                                t(
                                  'categories.noName',
                                )}
                            </strong>

                            <small>
                              {categoryDescription ||
                                t(
                                  'categories.noDescription',
                                )}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        {category.parent_id
                          ? t(
                              'categories.details.subcategory',
                            )
                          : t(
                              'categories.details.mainCategory',
                            )}
                      </td>

                      <td>
                        {category.parent_id
                          ? parentName || '-'
                          : '-'}
                      </td>

                      <td>
                        {formatNumber(
                          category.products_count ?? 0,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          category.sort_order ?? 0,
                          language,
                        )}
                      </td>

                      <td>
                        <CustomSelect
                          value={
                            category.is_active
                              ? 'active'
                              : 'inactive'
                          }
                          onChange={(value) =>
                            handleStatusChange(
                              category.id,
                              value,
                            )
                          }
                          className="category-status-select"
                          portal
                          ariaLabel={t(
                            'categories.table.status',
                          )}
                          options={[
                            {
                              value:
                                'active',
                              label:
                                t(
                                  'categories.status.active',
                                ),
                            },
                            {
                              value:
                                'inactive',
                              label:
                                t(
                                  'categories.status.inactive',
                                ),
                            },
                          ]}
                        />
                      </td>

                      <td>
                        <div className="categories-table__actions">
                          <Link
                            to={`/categories/${category.id}`}
                            aria-label={t(
                              'categories.actions.preview',
                            )}
                          >
                            <Eye size={17} />
                          </Link>

                          <Link
                            to={`/categories/${category.id}/edit`}
                            aria-label={t(
                              'categories.actions.edit',
                            )}
                          >
                            <Pencil size={17} />
                          </Link>

                          <button
                            type="button"
                            className="categories-table__delete"
                            onClick={() =>
                              handleDelete(category)
                            }
                            aria-label={t(
                              'categories.actions.delete',
                            )}
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="products-empty-state">
            <FolderTree size={34} />

            <h2>
              {t('categories.empty.title')}
            </h2>

            <p>
              {t(
                'categories.empty.description',
              )}
            </p>
          </div>
        )}

        {selectedMenuId &&
        !loading &&
        paginationTotal > 0 ? (
          <div className="categories-pagination">
            <div className="categories-pagination__summary">
              <span>
                {translateFallback(
                  t,
                  'common.showing',
                  'Showing',
                )}
              </span>

              <strong>
                {formatNumber(paginationStart, language)} -{' '}
                {formatNumber(paginationEnd, language)}
              </strong>

              <span>
                {translateFallback(
                  t,
                  'common.of',
                  'of',
                )}
              </span>

              <strong>
                {formatNumber(paginationTotal, language)}
              </strong>
            </div>

            <div className="categories-pagination__controls">
              <button
                type="button"
                className="categories-pagination__button"
                disabled={activePage <= 1}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.max(1, page - 1),
                  )
                }
              >
                {isRtl ? (
                  <ChevronRight size={17} />
                ) : (
                  <ChevronLeft size={17} />
                )}
                {translateFallback(
                  t,
                  'common.previous',
                  'Previous',
                )}
              </button>

              <div className="categories-pagination__pages">
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={
                      activePage === pageNumber
                        ? 'active'
                        : ''
                    }
                    onClick={() =>
                      setCurrentPage(pageNumber)
                    }
                  >
                    {formatNumber(pageNumber, language)}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="categories-pagination__button"
                disabled={activePage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(
                      totalPages,
                      page + 1,
                    ),
                  )
                }
              >
                {translateFallback(
                  t,
                  'common.next',
                  'Next',
                )}
                {isRtl ? (
                  <ChevronLeft size={17} />
                ) : (
                  <ChevronRight size={17} />
                )}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default Categories;